import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { formatScheduleBreaksForDisplay } from '@/lib/meeting-block-utils';
import { formatStudentGrades } from '@/lib/student-group-utils';
import { pluralize } from '@/lib/string-utils';
import { formatPlainDate, getPersonFirstNameWithLastNameInitial } from '@airreading/common';
import { adjustMinimalClassTimeAcrossTZ, StudentGroupWithScheduleAndLevel } from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';
import { formatStudentClassTimeAbbr } from '../../scheduling/schedules/-components/formatting';

interface CoachStudentGroupProps {
  classData: StudentGroupWithScheduleAndLevel;
  attendanceText?: React.ReactNode | string;
  teacherTimeZone?: string | null;
  'data-testid'?: string;
}

const CoachStudentGroup = ({
  classData,
  attendanceText,
  teacherTimeZone,
  'data-testid': dataTestId,
}: CoachStudentGroupProps) => {
  // Calculate duration from the first meeting block's time interval
  const firstMeetingBlock = classData.classTime.groupedMeetingBlocks?.[0];
  const duration = firstMeetingBlock
    ? Temporal.PlainTime.from(firstMeetingBlock.timeInterval.startPlainTime)
        .until(Temporal.PlainTime.from(firstMeetingBlock.timeInterval.endPlainTime))
        .total('minutes')
    : 'N/A';

  const students = classData.students.map((student) => getPersonFirstNameWithLastNameInitial(student)).join(', ');

  const maxPlannedAbsences = `${classData.maxPlannedAbsences} ${pluralize(classData.maxPlannedAbsences, 'class', 'classes')}`;

  const { schedule } = classData.classTime;
  const displayTimeZone = teacherTimeZone ?? schedule.timeZone;
  const schoolBreaks = formatScheduleBreaksForDisplay(schedule.scheduleBreaks) || '-';
  const classTimes = formatStudentClassTimeAbbr(
    adjustMinimalClassTimeAcrossTZ(classData.classTime, schedule.school.org.timeZone, displayTimeZone),
  );

  const baseTestId = dataTestId || `my-schedule-student-group`;

  return (
    <InfoCard headerText="">
      <InfoKeyValue label="School" value={schedule.school.name} data-testid={`${baseTestId}-school`} />
      <InfoKeyValue label="Students" value={students} className="mr-4" data-testid={`${baseTestId}-students`} />
      <InfoKeyValue label="Grades" value={formatStudentGrades(classData.grades)} data-testid={`${baseTestId}-grades`} />
      <InfoKeyValue label="Next lesson" value={classData.nextLesson?.name} data-testid={`${baseTestId}-next-lesson`} />
      <InfoKeyValue
        label={
          <>
            Class times <span className="font-medium">({displayTimeZone})</span>
          </>
        }
        className="mr-4"
        value={classTimes}
        data-testid={`${baseTestId}-class-times`}
      />
      <InfoKeyValue label="Duration" value={`${duration} mins`} data-testid={`${baseTestId}-duration`} />
      <InfoKeyValue
        label="Last class date"
        value={formatPlainDate(classData.classTime.schedule.endPlainDate)}
        data-testid={`${baseTestId}-last-class-date`}
      />
      <InfoKeyValue
        label="Sub request limit"
        value={maxPlannedAbsences}
        data-testid={`${baseTestId}-max-planned-absences`}
      />
      <InfoKeyValue
        label="Attendance"
        value={attendanceText}
        className="col-span-2"
        data-testid={`${baseTestId}-attendance`}
      />
      <InfoKeyValue
        label="School breaks (before last class)"
        value={schoolBreaks}
        data-testid={`${baseTestId}-school-breaks`}
      />
    </InfoCard>
  );
};

export default CoachStudentGroup;
